import { Command, Option } from 'commander';
import {
  loadEmbedder,
  getNodeEmbeddings,
  resolveEmbedModel,
  countPending,
  embeddingsCached,
  embeddingsPath,
  modelCacheInfo,
  clearModelCache,
  unavailableMessage,
  type EmbedUnavailable,
} from '../engine/embeddings.js';
import { embedderHealth, clearEmbedderHealth } from '../engine/embed-health.js';
import { applyGlobalOptions, readGlobal } from '../cli-options.js';
import { requireGraph, rootOf } from './util.js';
import { publishGraphToVgd } from '../runtime/vgd/publish.js';
import { c, info, json } from '../util/output.js';
import { ProgressBar } from '../util/progress.js';

function mb(bytes: number): string {
  if (bytes <= 0) return '0 MB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * `vg embed` — precompute the semantic index (node embeddings) so the next
 * `vg ask` is instant. The model is downloaded once per machine into a central,
 * home-folder cache (XDG `~/.cache/vibgrate/models`; no admin/sudo needed) shared
 * across all repos. Per-repo vectors are a binary `embeddings` file next to the
 * code map (global application store / in-repo `.vibgrate/`), not under
 * `.vibgrate/cache/` and not named after the model.
 *
 * `--where` shows the cache location/size; `--clear` removes the model. The
 * hidden `--bg` flag warms silently and never downloads (the `vg build` warm-up).
 */
export function registerEmbed(program: Command): void {
  const cmd = program
    .command('embed')
    .description('precompute the semantic index for instant `vg ask` (or --where / --clear)')
    .option('--where', 'show where the model is cached, and its size')
    .option('--clear', 'remove the downloaded model from the shared cache')
    // --bg: the silent background warm-up spawned by `vg build`. It skips the
    // model download by default; `--download` lets that background warm fetch
    // the model too, so a graph build starts the one-time download immediately
    // and the next `vg serve`/`vg ask` is instant instead of paying a cold load.
    .addOption(new Option('--bg').hideHelp())
    .addOption(new Option('--download').hideHelp())
    .action(async function (this: Command, opts: { where?: boolean; clear?: boolean; bg?: boolean; download?: boolean }) {
      const global = readGlobal(this);
      const modelId = resolveEmbedModel();

      // --where / --clear don't need a built graph.
      if (opts.where) return showWhere(rootOf(global), modelId, global.json);
      if (opts.clear) return doClear(global.json);

      const { graph } = requireGraph(global);
      const root = rootOf(global);
      const bg = opts.bg === true;
      const speak = !bg && !global.json;

      if (global.offline) {
        if (global.json) json({ embedded: 0, model: modelId, semantic: false, reason: 'local' });
        else if (speak) info(c.dim('vg embed · semantic is off (--local) — lexical search only'));
        return;
      }
      if (countPending(graph, root, modelId) === 0) {
        await warmDaemonIndex(root, global.graph);
        if (global.json) json({ embedded: 0, pending: 0, upToDate: true, model: modelId, semantic: true });
        else if (speak) info(`${c.cyan('vg embed')} · semantic index already up to date`);
        return;
      }

      // Same guard as the LSP: a crashed native backend would take this
      // process down too, which is what made the background warm-up exit by
      // signal ("code null") on every retry instead of reporting anything.
      const health = await embedderHealth();
      if (health.status === 'crashed') {
        if (global.json) json({ embedded: 0, model: modelId, semantic: false, reason: 'native-crash', signal: health.signal });
        else if (speak) info(c.dim(`vg embed · ${health.message}`));
        return;
      }

      let reason: EmbedUnavailable | undefined;
      let detail: string | undefined;
      const embedder = await loadEmbedder({
        local: global.offline,
        // Background warm-ups skip the download unless explicitly asked to fetch
        // (`--bg --download`, spawned by `vg build` so the model is ready by the
        // time `vg serve`/`vg ask` needs it).
        noDownload: bg && opts.download !== true,
        showDownloadProgress: !bg,
        onUnavailable: (r, d) => {
          reason = r;
          detail = d;
        },
      });
      if (!embedder) {
        // Calm, specific fallback — never a stack trace. But never *silent*
        // either: a warm-up that reports plain success while nothing was
        // embedded is what makes a host say "semantic search ready" and then
        // answer every question lexically. `--json` carries the reason so the
        // host can repair (or say so honestly); `--bg` says one dim line.
        if (global.json) {
          json({ embedded: 0, model: modelId, semantic: false, reason: reason ?? 'init-failed', detail: detail ?? null });
        } else if (reason) {
          info(c.dim(`vg embed · ${unavailableMessage(reason)}${detail ? ` (${detail})` : ''}`));
        }
        return;
      }

      const pending = countPending(graph, root, modelId);
      const bar = speak ? new ProgressBar(c.dim('embedding')) : undefined;
      await getNodeEmbeddings(graph, embedder, root, bar ? (d, t) => bar.update(d, t) : undefined);
      bar?.done();
      // The on-disk vectors are complete now. A running vgd seeds its slot
      // index straight from them, so this is what turns `vg daemon status`
      // from "0/1 index(es) ready" into a warm daemon — without either side
      // embedding the same repo twice.
      await warmDaemonIndex(root, global.graph);
      if (global.json) json({ embedded: pending, model: modelId, semantic: true });
      else if (speak) info(`${c.green('✔')} vg embed · semantic index ready (~${pending} node(s) · model ${modelId})`);
    });
  applyGlobalOptions(cmd);
}

/**
 * Tell a running vgd that this repo's vectors are on disk, so it can warm the
 * matching graph slot's index from them. Best-effort: no daemon is a supported
 * configuration, and `vg embed` must not fail because of one.
 */
async function warmDaemonIndex(root: string, graphPath?: string): Promise<void> {
  await publishGraphToVgd(root, { graphPath, warmSemantic: true });
}

function showWhere(root: string, modelId: string, asJson?: boolean): void {
  const m = modelCacheInfo(modelId);
  const repoCached = embeddingsCached(root, modelId);
  const repoPath = embeddingsPath(root);
  if (asJson) {
    json({
      model: modelId,
      cacheDir: m.dir,
      present: m.present,
      bytes: m.bytes,
      repoEmbeddings: repoCached,
      embeddingsPath: repoPath,
    });
    return;
  }
  info(`${c.cyan('vg embed')} · model cache`);
  info(`  model     ${c.bold(modelId)}`);
  info(`  shared at ${m.dir}`);
  info(`            ${m.present ? c.green(`present · ~${mb(m.bytes)}`) : c.dim('not downloaded yet (fetched on first semantic ask)')}`);
  info(`  this repo ${repoCached ? c.green('embeddings cached') : c.dim('not embedded yet — run `vg embed`')}`);
  info(`            ${c.dim(repoPath)}`);
  info(c.dim('  no admin/sudo needed — the model cache is in your home folder, shared across all repos.'));
  info(c.dim('  vectors sit next to the code map (binary `embeddings`), not under .vibgrate/cache/.'));
  info(c.dim('  turn off: run with --local · relocate: XDG_CACHE_HOME=<dir> · remove: vg embed --clear'));
}

function doClear(asJson?: boolean): void {
  const before = modelCacheInfo();
  const freed = clearModelCache();
  // Forget any crash verdict too: `--clear` is what someone runs after fixing
  // a broken install, and a sticky verdict would keep semantic off.
  clearEmbedderHealth();
  if (asJson) {
    json({ removed: before.present, bytesFreed: freed, dir: before.dir });
    return;
  }
  if (!before.present || freed === 0) {
    info(`${c.cyan('vg embed')} · no model in the shared cache — nothing to remove (${before.dir})`);
    return;
  }
  info(`${c.green('✔')} vg embed · removed the shared model (~${mb(freed)}) from ${before.dir}`);
  info(c.dim('  repos use fast lexical search until the next semantic ask re-downloads it.'));
}
